<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-card
    id="hours"
    :border="isSpecialist"
    class="mb-4"
    flat
    tile
  >
    <v-card-title class="tab-title d-flex">
      <StandardButton
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('Hours of operation') }}
      </h2>
    </v-card-title>
    <v-divider />
    <v-card-text class="my-4">
      <div class="mb-4">
        <v-col class="fs-18 ma-0 pa-0">
          {{ $bct('Confirm the days of the week and hours you are open.') }}
        </v-col>
        <v-row class="mt-2">
          <LabeledSelect
            v-model="provider.closed_status"
            :bg-color="provider.closed_status === 'Open' ? 'green' : 'red'"
            :schema-id="provider.schema_id"
            aria-label="Closed status"
            cols="12"
            data-cy="closed_status"
            field="closed_status"
            md="5"
            prepend-inner-icon="check_circle"
            variant="solo-filled"
            global
            rounded
          />
          <LabeledSelect
            v-show="provider.closed_status === 'Closed'"
            v-model="provider.closure_reason"
            :schema-id="provider.schema_id"
            aria-label="Closure reason"
            cols="12"
            field="closure_reason"
            md="4"
            message="Closure reason"
            global
          />
          <LabeledSelect
            v-show="provider.closed_status === 'Temporarily closed'"
            v-model="provider.temporary_closure_reason"
            :schema-id="provider.schema_id"
            aria-label="Temporary closure reason"
            cols="12"
            field="temporary_closure_reason"
            md="4"
            message="Temporary closure reason"
            global
          />
          <LabeledDatePicker
            v-if="provider.closed_status === 'Temporarily closed'"
            v-model="provider.open_date"
            @input="save()"
            :schema-id="provider.schema_id"
            aria-label="Choose date you will open"
            cols="12"
            field="open_date"
            md="3"
          />
        </v-row>
      </div>

      <v-row>
        <LabeledSelect
          v-model="provider.program_season"
          id="manager_programs_operate"
          :aria-label="`When do your ${terms.programs.toLowerCase()} operate?`"
          :message="programSeasonMessage"
          :schema-id="provider.schema_id"
          data-cy="program_season"
          field="program_season"
          global
          tracked
        />
        <LabeledSelect
          v-model="provider.program_types"
          id="manager_program_types"
          :aria-label="`What type of ${terms.program.toLowerCase()} is this?`"
          :message="programTypesMessage"
          :schema-id="provider.schema_id"
          data-cy="program_types"
          field="program_types"
          global
          tracked
        />
      </v-row>

      <v-row>
        <v-col class="fw-500">
          {{ $bct('When are you open?') }}
        </v-col>
      </v-row>

      <v-alert
        v-if="hoursValidationError"
        class="my-3"
        density="compact"
        type="error"
      >
        {{ $bct(hoursValidationError) }}
      </v-alert>

      <v-row
        v-for="(day, index) in hours"
        :key="index"
        class="d-flex align-center justify-space-between flex-wrap flex-md-nowrap mb-2"
        dense
      >
        <v-col
          class="d-flex align-center"
          cols="6"
          md="2"
        >
          <span
            :id="'provider-hours-day-' + index"
            class="c-black bc-body"
          >
            {{ $bct(getDayText(day)) }}
          </span>
        </v-col>

        <v-col
          :class="['d-flex align-center', $vuetify.display.smAndDown && 'justify-end']"
          cols="6"
          md="2"
        >
          <v-switch
            v-model="hours[index].included"
            @update:model-value="save"
            :aria-describedby="'provider-hours-day-' + index"
            :disabled="locked"
            :label="$bct(getOpenClosedLabel(index))"
            color="primary"
            role="switch"
            variant="filled"
            data-cy
            hide-details
            inset
            tile
          />
        </v-col>

        <v-col
          class="d-flex align-center gap-2 flex-nowrap"
          cols="12"
          md="8"
        >
          <template v-if="hours[index].included">
            <div class="d-flex flex-column flex-grow-1 me-1 min-w-0">
              <span
                :id="'provider-hours-open-a11y-' + index"
                class="text-caption text-medium-emphasis mb-1"
              >
                {{ $bct('Opening time') }}, {{ militaryValueToDisplay(hours[index].open) }}
              </span>
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="hours[index].open"
                @update:model-value="save"
                :aria-labelledby="
                  'provider-hours-day-' + index + ' provider-hours-open-a11y-' + index
                "
                :items="getTimeOptions(0, 1440, 5)"
                class="flex-grow-1"
                density="compact"
                item-title="title"
                item-value="value"
                hide-details
              />
            </div>
            <div class="d-flex flex-column flex-grow-1 min-w-0">
              <span
                :id="'provider-hours-close-a11y-' + index"
                class="text-caption text-medium-emphasis mb-1"
              >
                {{ $bct('Closing time') }}, {{ militaryValueToDisplay(hours[index].close) }}
              </span>
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="hours[index].close"
                @update:model-value="save"
                :aria-labelledby="
                  'provider-hours-day-' + index + ' provider-hours-close-a11y-' + index
                "
                :items="getTimeOptions(0, 1440, 5)"
                class="flex-grow-1"
                density="compact"
                item-title="title"
                item-value="value"
                hide-details
              />
            </div>
          </template>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          class="ma-0 pa-0"
          cols="12"
        >
          <LabeledTextarea
            v-model="provider.hours_other_description"
            :schema-id="provider.schema_id"
            aria-label="Add notes about hours"
            field="hours_other_description"
            global
          />
        </v-col>
      </v-row>

      <v-row
        v-for="field in getAppendedHours()"
        :key="field"
      >
        <CustomField
          :field="field"
          :model-value="provider"
          :schema-id="provider.schema_id"
          :show-title="true"
          class="fs-16 mb-2"
          mode="edit"
          global
        />
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';

import assets from '@/shared/services/assets';
import CustomField from '@/shared/components/CustomField.vue';
import { getTimeOptions, parseMilToTime, parseTimeToString } from '@/shared/services/time-parser';
import LabeledDatePicker from '@/shared/components/form/LabeledDatePicker.vue';
import LabeledSelect from '@/shared/components/form/LabeledSelect.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import useRole from '@/shared/composables/useRole';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');

const display = useDisplay();
const { isSpecialist, role } = useRole();
const store = useStore();
const { terms } = useTerms();

defineProps({
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const provider = defineModel({ type: undefined });

const emit = defineEmits(['progress', 'toggleProviderProfileNav']);

const hoursValidationError = ref(null);
const hours = ref([
  {
    day: 0,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 1,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 2,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 3,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 4,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 5,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 6,
    open: 700,
    close: 1800,
    included: false,
  },
]);

watch(
  () => provider.value.hours,
  () => {
    setHours();
  },
  { deep: true, immediate: true },
);

const locked = computed(() => !getSchema().definition.properties.hours.editRoles.includes(role));

const programSeasonMessage = computed(() => {
  return (
    getSchema().definition.properties.program_season?.alias ||
    `When do your ${terms.value.programs.toLowerCase()} operate?`
  );
});

const programTypesMessage = computed(() => {
  return (
    getSchema().definition.properties.program_types?.alias ||
    `What type of ${terms.value.program.toLowerCase()} is this?`
  );
});

function getAppendedHours() {
  return getSchema().meta.hours.append;
}

function getDayText(day) {
  return assets.assets.weekdays[day.day];
}

function militaryValueToDisplay(militaryValue) {
  if (militaryValue == null || militaryValue === '') return '';
  return parseTimeToString(parseMilToTime(Number(militaryValue)));
}

function getOpenClosedLabel(index) {
  if (display.smAndDown.value) return null;

  return hours.value[index].included ? 'Open' : 'Closed';
}

function getSchema() {
  return store.state.schemas[provider.value.schema_id];
}

function save() {
  const includedDays = hours.value.filter((day) => day.included);
  const invalidDay = includedDays.find((day) => day.open >= day.close);
  if (invalidDay) {
    hoursValidationError.value = 'Open time must be earlier than close time for each day.';
    return;
  }
  hoursValidationError.value = null;
  provider.value.hours = includedDays.map((day) => ({
    day: day.day,
    open: day.open,
    close: day.close,
  }));

  emit('progress', provider.value.hours ? 100 : 0);
}

function setHours() {
  if (provider.value.hours) {
    emit('progress', 100);
    hours.value = hours.value.map((hour) => {
      const oldDay = provider.value.hours.find((providerHour) => providerHour.day === hour.day);
      const newDay = hour;
      if (oldDay) {
        newDay.close = oldDay.close;
        newDay.included = true;
        newDay.open = oldDay.open;
        return newDay;
      }
      newDay.included = false;
      return newDay;
    });
  }
}
</script>
